import { motion } from "framer-motion";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { MapPin } from "lucide-react";

const STEP_ANIMATION = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const SPECIALIST_ITEM_OPTIONS = ["Piano", "Antiques", "Artwork", "Fragile Items", "Safe/Heavy Items"];

export const StepServices = ({ formData, updateFormData, toggleArrayItem }) => (
  <motion.div key="step4" {...STEP_ANIMATION} className="space-y-6">
    <div className="text-center mb-8">
      <div className="w-16 h-16 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
        <MapPin className="w-8 h-8 text-[#FF5A1F]" />
      </div>
      <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A]">Additional Services</h2>
      <p className="text-slate-600 mt-2">Need any extras?</p>
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border border-slate-200">
        <div>
          <div className="font-semibold text-[#0F172A]">Packing Service</div>
          <div className="text-sm text-slate-600">We pack everything for you</div>
        </div>
        <Checkbox
          checked={formData.packing_required}
          onCheckedChange={(checked) => updateFormData("packing_required", checked)}
          data-testid="packing-checkbox"
        />
      </div>

      <div className="flex items-center justify-between p-4 border border-slate-200">
        <div>
          <div className="font-semibold text-[#0F172A]">Dismantling & Reassembly</div>
          <div className="text-sm text-slate-600">Furniture dismantling and rebuilding</div>
        </div>
        <Checkbox
          checked={formData.dismantling_required}
          onCheckedChange={(checked) => updateFormData("dismantling_required", checked)}
          data-testid="dismantling-checkbox"
        />
      </div>

      <div className="flex items-center justify-between p-4 border border-slate-200">
        <div>
          <div className="font-semibold text-[#0F172A]">Storage Required</div>
          <div className="text-sm text-slate-600">Secure storage for your items</div>
        </div>
        <Checkbox
          checked={formData.storage_required}
          onCheckedChange={(checked) => updateFormData("storage_required", checked)}
          data-testid="storage-checkbox"
        />
      </div>

      {formData.storage_required && (
        <div className="pl-4">
          <Label className="label-text">Weeks of Storage</Label>
          <Select value={String(formData.storage_weeks)} onValueChange={(v) => updateFormData("storage_weeks", parseInt(v))}>
            <SelectTrigger className="h-12" data-testid="storage-weeks-select">
              <SelectValue placeholder="Select weeks" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 8, 12].map((num) => (
                <SelectItem key={num} value={String(num)}>{num} week{num > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>

    <div className="pt-4">
      <Label className="label-text">Specialist Items</Label>
      <p className="text-xs text-slate-500 mb-2">Note: Specialist items require a survey for accurate pricing</p>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {SPECIALIST_ITEM_OPTIONS.map((item) => (
          <button
            key={item}
            onClick={() => toggleArrayItem("specialist_items", item)}
            className={`p-3 text-sm border transition-all ${
              formData.specialist_items.includes(item)
                ? "border-[#FF5A1F] bg-[#FF5A1F]/5 text-[#0F172A]"
                : "border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`specialist-${item.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>

    <div className="pt-4">
      <Label className="label-text">Additional Notes</Label>
      <Textarea
        placeholder="Any other details we should know..."
        value={formData.notes}
        onChange={(e) => updateFormData("notes", e.target.value)}
        className="min-h-[100px]"
        data-testid="notes-textarea"
      />
    </div>
  </motion.div>
);
